import {
  Component,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges
} from '@angular/core';
import {SearchParameters} from '../../core/definitions/search-parameters';
import {UntypedFormControl} from '@angular/forms';
import {TreeNode, TreeNodeChildren} from './tree-node/tree-node.component';
import {Subscription} from 'rxjs';
import {debounceTime, map, startWith, tap} from 'rxjs/operators';
import {SearchObject} from '../../core/definitions/search-object';
import {SearchService} from "../../core/search.service";
import {CommonsService} from "../../core/commons.service";

@Component({
    selector: 'app-primus-solr-tree-view',
    templateUrl: './primus-solr-tree-view.component.html',
    styleUrls: ['./primus-solr-tree-view.component.scss'],
    standalone: false
})
export class PrimusSolrTreeViewComponent implements OnInit, OnChanges, OnDestroy {
  // Injections
  private readonly commons: CommonsService = inject(CommonsService);
  private readonly searchService: SearchService = inject(SearchService);

  private readonly ROOT_NODE_ID: string = 'root';
  private readonly searchInputChangeSubscription: Subscription;

  // GUI elements/controls
  @Input() public readonly searchInputLabel: string = 'TRANS__SOLR_TREE_VIEW__SEARCH_INPUT_LABEL';
  @Input() public readonly topNodeLabel: string = 'TRANS__SOLR_TREE_VIEW__ALL_NODES_LABEL';

  // Config
  @Input() public query: string;
  @Input() public tabName: string;
  @Input() public idProp = 'artifact_id';
  @Input() public labelProp = 'artifact_name';
  @Input() public parentIdParentQueryPart = '';
  @Input() public contextIdProp = '';
  @Input() public parentIdProp = 'parent_id';
  @Input() public parentIdFlProp = 'parent_id';
  @Input() public fullPathIdProp = 'm_path';
  @Input() public fullPathLabelProp = 'full_path';
  @Input() public fullPathIdSeparator = '/';
  @Input() public fullPathLabelSeparator = this.commons.getPathSeparatorSymbol();
  @Input() public secondaryLabelProp?: string;
  @Input() public selectedNode: TreeNode | null;
  @Input() public isLeafProp = 'is_leaf';
  /** Hide the "search" tab, leaving only the hierarchy browser. */
  @Input() public readonly hideSearchTab = false;

  // Outputs
  @Output() public readonly selectionChanged: EventEmitter<TreeNode | null> = new EventEmitter<TreeNode | null>();

  // Internal state
  readonly pageSize: number = 3000;
  readonly searchControl: UntypedFormControl = new UntypedFormControl();
  searchChildren: TreeNodeChildren = {};

  readonly rootNode: TreeNode = {id: this.ROOT_NODE_ID, label: this.topNodeLabel};
  children: TreeNodeChildren = {};

  loading: TreeNode | null = null;

  /**
   * Whether or not a search is in progress
   * @type {boolean}
   */
  searching = false;
  currentSearchPageIndex = 0;
  enableSearchLoadMoreButton = false;

  constructor() {
    this.searchInputChangeSubscription = this.searchControl.valueChanges.pipe(
      startWith(''),
      debounceTime(500),
      map(val => val || ''),
      tap(() => {
        this.currentSearchPageIndex = 0;
      })
    ).subscribe(() => this.search());
  }

  public ngOnInit(): void {
    if (!this.selectedNode) {
      this.selectedNode = this.rootNode;
    }
    this.loadChildren(this.rootNode).then();
  }


  public ngOnChanges(changes: SimpleChanges): void {
    if (changes.hasOwnProperty('topNodeLabel')) {
        this.rootNode.label = changes.topNodeLabel.currentValue;
    }
  }

  public ngOnDestroy(): void {
    if (!!this.searchInputChangeSubscription && !this.searchInputChangeSubscription.closed) {
      this.searchInputChangeSubscription.unsubscribe();
    }
  }

  async loadChildren(node: TreeNode, forceReload: boolean = false): Promise<void> {
    if (!node || (!forceReload && !!this.children[node.id])) {
      return;
    }
    try {
      this.loading = node;
      const isRootNode = node.id === this.ROOT_NODE_ID;
      let query: string;
      if (isRootNode) {
        query = `${this.parentIdParentQueryPart}-${this.parentIdProp}:*`;
      } else {
        query = `${this.parentIdParentQueryPart}${this.parentIdProp}:"${node.id}"`;
      }
      // const query = [
      //   isRootNode ? '-' : '',
      //   this.parentIdParentQueryPart,
      //   this.parentIdProp,
      //   ':',
      //   isRootNode ? '*)' : `"${node.id}")`,
      // ].join('');
      const res = await this.searchService.search({
        query,
        fq: [this.query],
        fl: this.getSolrFieldList(),
        sort: `${this.labelProp} asc`,
        rows: this.pageSize
      } as SearchParameters);
      this.children[node?.id] = (res?.artifacts || []).map(this.mapArtifactToTreeNode.bind(this)).sort(this.naturalSort) as TreeNode[];
    } catch (e) {
      console.error('Unable to load children', e);
      this.children[node?.id] = [];
    } finally {
      this.loading = null;
    }
  }

  // If parentIdFlProp contains 'parentFilter', this means that the field is found within child documents. We then
  // need to return all fields plus the child document field due to technical limitations in Solr.
  // If parentIdFlProp does not contain 'parentFilter', we can just return the fields we need.
  private getSolrFieldList() {
    return this.parentIdFlProp.includes('parentFilter') ? [`*,${this.parentIdFlProp}`] : [
      this.idProp,
      this.labelProp,
      this.secondaryLabelProp,
      this.fullPathIdProp,
      this.fullPathLabelProp,
      this.isLeafProp,
      this.parentIdFlProp];
  }

  private readonly naturalSort = (a: TreeNode, b: TreeNode): number => {
    return new Intl.Collator('nb-NO', {numeric: true, sensitivity: 'base'}).compare(a.label, b.label)
  }

  async search(nextPage: boolean = false): Promise<void> {
    const value = this.searchControl.value;
    if (!value) {
      this.searchChildren = this.children;
      return;
    }
    try {
      if (nextPage) {
        this.currentSearchPageIndex++;
      }
      this.searching = true;
      const params = {
        query: `${this.labelProp}:*${value}*`,
        fq: [this.query],
        fl: this.getSolrFieldList(),
        rows: this.pageSize,
        start: this.pageSize * this.currentSearchPageIndex
      } as SearchParameters;
      const res = await this.searchService.search(params);
      const numberOfLoadedOptions = this.pageSize * (this.currentSearchPageIndex + 1);
      this.enableSearchLoadMoreButton = numberOfLoadedOptions < (res?.search_count || 0);
      const matches = res?.artifacts || [];
      const children: TreeNodeChildren = nextPage ? this.searchChildren : {
        [this.ROOT_NODE_ID]: []
      };

      for (const match of matches) {
        let parentIds: string[] = [];
        let parentNames: string[] = [];
        if (match._childDocuments_) {
          for (const childDoc of match._childDocuments_) {
            const pIds = (childDoc[this.fullPathIdProp] || '')
              .split(this.fullPathIdSeparator)
              .filter(v => !!v);
            // Add path ids except the context id
            const contextId = childDoc[this.contextIdProp];
            parentIds.push(...pIds.filter(pId => pId !== contextId));
            const pNames = ((childDoc[this.fullPathLabelProp] || '') as string)
              .split(this.fullPathLabelSeparator)
              .slice(0, -1)
              .filter(v => !!v);
            parentNames.push(...pNames);
          }
        } else {
          parentIds = (match[this.fullPathIdProp] || '')
            .split(this.fullPathIdSeparator)
            .filter(v => !!v);
          parentNames = ((match[this.fullPathLabelProp] || '') as string)
            .split(this.fullPathLabelSeparator)
            .slice(0, -1)
            .filter(v => !!v);
        }

        const normalizedParentIds: string[] = [];
        const normalizedParentNames: string[] = [];
        for (let i = 0; i < parentIds.length; i++) {
          const parentId = parentIds[i];
          if (!parentId || normalizedParentIds[normalizedParentIds.length - 1] === parentId) {
            continue;
          }
          normalizedParentIds.push(parentId);
          normalizedParentNames.push(parentNames[i] || parentId);
        }
        parentIds = normalizedParentIds;
        parentNames = normalizedParentNames;

        if (parentIds[0] !== this.rootNode.id) {
          parentIds.unshift(this.rootNode.id);
          parentNames.unshift(this.rootNode.label);
        } else {
          parentNames[0] = this.rootNode.label;
        }

        if (parentIds.length !== parentNames.length) {
          console.warn('Search returned node where parent names and parent ids are inconsistent:', match);
        }

        let current = this.mapArtifactToTreeNode(match);
        if (parentIds[parentIds.length - 1] === current.id) {
          parentIds = parentIds.slice(0, -1);
          parentNames = parentNames.slice(0, -1);
        }

        for (let i = parentIds.length - 1; i >= 0; i--) {
          const id = parentIds[i];
          const currentChildren = children[id];
          if (this.wouldCreateCycle(children, id, current.id)) {
            console.warn('Prevented cyclic tree link while processing search result:', {parentId: id, childId: current.id, match});
          } else if (!Array.isArray(currentChildren)) {
            children[id] = [current];
          } else if (!currentChildren.some(c => c.id === current.id)) {
            currentChildren.push(current);
          }
          current = {id, label: parentNames[i] || id};
        }
      }

      this.searchChildren = children;
    } catch (e) {
      console.error('Error occurred while searching in locations', e);
    } finally {
      this.searching = false;
    }
  }

  clearSearch(): void {
    this.searchControl.reset('', {emitEvent: false});
  }

  onSelect(node: TreeNode): void {
    this.selectionChanged.emit(node?.id === this.ROOT_NODE_ID ? null : node);
  }

  private wouldCreateCycle(children: TreeNodeChildren, parentId: string, childId: string): boolean {
    if (!parentId || !childId || parentId === childId) {
      return true;
    }
    const visited: Set<string> = new Set<string>();
    const stack: string[] = [childId];

    while (stack.length > 0) {
      const currentId = stack.pop();
      if (!currentId || visited.has(currentId)) {
        continue;
      }
      if (currentId === parentId) {
        return true;
      }
      visited.add(currentId);

      const currentChildren = children[currentId];
      if (!Array.isArray(currentChildren)) {
        continue;
      }
      for (const child of currentChildren) {
        if (child?.id && !visited.has(child.id)) {
          stack.push(child.id);
        }
      }
    }

    return false;
  }

  private mapArtifactToTreeNode(so: SearchObject): TreeNode {
    return {
      id: so[this.idProp],
      label: so[this.labelProp],
      secondaryLabel: this.secondaryLabelProp ? so[this.secondaryLabelProp] : '',
      is_leaf: so[this.isLeafProp],
    } as TreeNode;
  }
}
